import {CountryCode} from "../reducers/ticket";
import { useContext } from "react";
import { TicketDetailsContext } from '../components/TicketDetailsContext';


export const useAvailableLanguages = (): string[] => {
  // Try to get the market from context (this might throw an exception if used outside the provider)
  const { market : contextMarket} = useContext(TicketDetailsContext);
  return marketConfigs[contextMarket].supportedLanguages || ["de"];
};

export type LinkKey = "termsLink" | "privacyLink";

export interface MarketConfig {
  code: CountryCode;
  supportedLanguages: string[];
  links: Record<LinkKey, {
    [lang: string]: string;
  }>;
}

// Define configuration for each market
const marketConfigs: Record<CountryCode, MarketConfig> = {
  CH: {
    code: CountryCode.CH,
    supportedLanguages: ["en", "fr", "de", "it"],
    links: {
      termsLink: {
        en: "https://mybeppo.ch/en/general-terms-and-conditions/",
        fr: "https://mybeppo.ch/fr/cgv/",
        de: "https://mybeppo.ch/agb/",
        it: "https://mybeppo.ch/it/cgc/"
      },
      privacyLink: {
        en: "https://mybeppo.ch/en/privacy-policy/",
        fr: "https://mybeppo.ch/fr/protection-des-donnees/",
        de: "https://mybeppo.ch/datenschutzerklaerung/",
        it: "https://mybeppo.ch/it/informativa-sulla-privacy/"
      }
    }
  },
  DE: {
    code: CountryCode.DE,
    supportedLanguages: ["en", "de"],
    links: {
      termsLink: {
        en: "https://mybeppo.de/gtc/",
        de: "https://mybeppo.de/agb/"
      },
      privacyLink: {
        en: "https://mybeppo.de/privacy-policy/",
        de: "https://mybeppo.de/datenschutzerklaerung/",
      }
    }
  },
  AT: {
    code: CountryCode.AT,
    supportedLanguages: ["en", "de"],
    links: {
      termsLink: {
        en: "https://mybeppo.at/gtc/",
        de: "https://mybeppo.at/agb/"
      },
      privacyLink: {
        en: "https://mybeppo.at/privacy-policy/",
        de: "https://mybeppo.at/datenschutzerklaerung/",
      }
    }
  }
};

/**
 * Determines the market based on the hostname
 * @returns The market code (CH, DE, AT)
 */
export function detectMarket(): CountryCode {
  const hostname = window.location.hostname;

  if (hostname.includes('.ch') || hostname.includes('mybeppo.ch')) {
    return CountryCode.CH;
  } else if (hostname.includes('.de') || hostname.includes('mybeppo.de')) {
    return CountryCode.DE;
  } else if (hostname.includes('.at') || hostname.includes('mybeppo.at')) {
    return CountryCode.AT;
  }

  // Default to CH if can't detect
  return CountryCode.CH;
}

/**
 * Gets market configuration for the provided market code
 * @param marketCode The market code
 * @returns The market configuration
 */
export function getMarketConfig(marketCode: CountryCode): MarketConfig {
  return marketConfigs[marketCode];
}

/**
 * Gets a link for the specific market and language
 * @param marketCode The market code
 * @param linkKey The link key (e.g., "termsLink", "privacyLink")
 * @param language The language code
 * @returns The URL for the requested link
 */
export function getMarketLink(marketCode: CountryCode, linkKey: LinkKey, language: string): string {
  const market = marketConfigs[marketCode];

  if (!market || !market.links[linkKey]) {
    console.warn(`Link "${linkKey}" not defined for market "${marketCode}"`);
    return "";
  }

  // If language not supported, fall back to first available language
  if (!market.links[linkKey][language]) {
    const fallbackLang = market.supportedLanguages[0];
    console.warn(`Language "${language}" not supported for link "${linkKey}" in market "${marketCode}", falling back to "${fallbackLang}"`);
    return market.links[linkKey][fallbackLang];
  }

  return market.links[linkKey][language];
}

/** Zendesk "new request" form per language; one help center serves all markets. */
const zendeskUrls: { [lang: string]: string } = {
  de: "https://helpcenter.mybeppo.ch/hc/de/requests/new",
  fr: "https://helpcenter.mybeppo.ch/hc/fr/requests/new",
  it: "https://helpcenter.mybeppo.ch/hc/it/requests/new",
  en: "https://helpcenter.mybeppo.ch/hc/en-ch/requests/new"
};

export function getZendeskUrl(lang: string): string {
  return zendeskUrls[lang] || zendeskUrls.en;
}
